import { Link, useLocation, useRouter } from "@tanstack/react-router";
import {
  BarChart3,
  ClipboardCheck,
  ClipboardList,
  FolderOpen,
  LayoutDashboard,
  LogOut,
  Menu,
  Settings,
} from "lucide-react";
import { useMemo } from "react";
import { Button } from "@/components/ui/button";
import { signOut, useSession } from "@/lib/auth-client";
import { cn } from "@/lib/utils";
import { useSidebar } from "./SidebarContext";
import { ThemeToggle } from "./ThemeToggle";

interface NavItem {
  label: string;
  icon: typeof LayoutDashboard;
  href: string;
  disabled?: boolean;
}

export function TopNav() {
  const { data: session } = useSession();
  const router = useRouter();
  const location = useLocation();
  const { isMobile, mobileOpen, setMobileOpen } = useSidebar();

  const navItems = useMemo<NavItem[]>(() => {
    const role = session?.user?.role ?? "";
    const items: NavItem[] = [
      { label: "My Dashboard", icon: LayoutDashboard, href: "/dashboard" },
      { label: "Projects", icon: FolderOpen, href: "/dashboard/projects" },
    ];

    // Compliance navigation
    if (role === "admin" || role === "compliance_manager") {
      items.push(
        { label: "Templates", icon: ClipboardList, href: "/dashboard/templates" },
        { label: "Audits", icon: ClipboardCheck, href: "/dashboard/audits" },
        { label: "Reports", icon: BarChart3, href: "/dashboard/reports" },
      );
    } else if (role === "compliance_auditor") {
      items.push({ label: "Audits", icon: ClipboardCheck, href: "/dashboard/audits" });
    } else if (role === "stakeholder") {
      items.push({ label: "Reports", icon: BarChart3, href: "/dashboard/reports" });
    }

    if (role === "admin") {
      items.push({ label: "Settings", icon: Settings, href: "/dashboard/settings" });
    }

    return items;
  }, [session?.user?.role]);

  async function handleSignOut() {
    await signOut();
    router.navigate({ to: "/login" });
  }

  function handleNavClick() {
    if (isMobile) {
      setMobileOpen(false);
    }
  }

  function isItemActive(item: NavItem) {
    return item.href === "/dashboard"
      ? location.pathname === "/dashboard"
      : location.pathname.startsWith(item.href);
  }

  function navLinkClassName(item: NavItem, isActive: boolean) {
    return cn(
      "flex items-center gap-2 rounded-md px-3 py-2 text-sm font-medium transition-colors",
      isActive ? "bg-sidebar-accent text-sidebar-primary" : "text-sidebar-foreground/60",
      item.disabled && "pointer-events-none opacity-40",
      !item.disabled && !isActive && "hover:bg-sidebar-accent hover:text-sidebar-foreground",
    );
  }

  return (
    <header className="flex h-16 items-center gap-4 border-b border-sidebar-border bg-sidebar px-4 md:px-6">
      {isMobile && (
        <Button
          variant="ghost"
          size="icon"
          onClick={() => setMobileOpen(true)}
          className="shrink-0 text-sidebar-foreground"
        >
          <Menu className="h-5 w-5" />
        </Button>
      )}

      <span className="font-display text-lg font-bold text-sidebar-foreground leading-tight shrink-0">
        Project Adherence
      </span>

      <div className="hidden md:flex flex-1 min-w-0 overflow-x-auto [&::-webkit-scrollbar]:hidden [-ms-overflow-style:none] [scrollbar-width:none]">
        <nav className="flex items-center gap-1">
          {navItems.map((item) => {
            const isActive = isItemActive(item);
            return (
              <Link
                key={item.label}
                to={item.href}
                onClick={handleNavClick}
                className={navLinkClassName(item, isActive)}
              >
                <item.icon className="h-4 w-4 shrink-0" />
                <span>{item.label}</span>
                {item.disabled && (
                  <span className="ml-auto text-[10px] uppercase tracking-wider text-muted-foreground">
                    Soon
                  </span>
                )}
              </Link>
            );
          })}
        </nav>
      </div>

      <div className="flex-1 md:flex-none" />

      <div className="flex items-center gap-3 shrink-0">
        <ThemeToggle />

        {!isMobile && session?.user && (
          <div className="text-right leading-tight">
            <p className="text-sm font-medium text-sidebar-foreground truncate max-w-[160px]">
              {session.user.name}
            </p>
            <p className="text-xs text-muted-foreground truncate max-w-[160px]">
              {session.user.email}
            </p>
          </div>
        )}

        {session?.user && (
          <div className="flex h-8 w-8 items-center justify-center rounded-full bg-primary text-primary-foreground text-sm font-medium shrink-0">
            {session.user.name?.charAt(0).toUpperCase() ?? "U"}
          </div>
        )}

        <Button
          variant="ghost"
          size="icon"
          onClick={handleSignOut}
          className="shrink-0 text-sidebar-foreground/60 hover:text-sidebar-foreground"
          aria-label="Sign out"
        >
          <LogOut className="h-4 w-4" />
        </Button>
      </div>

      {isMobile && mobileOpen && (
        <>
          {/* Backdrop */}
          <div
            className="fixed inset-x-0 top-16 bottom-0 z-40 bg-black/50"
            onClick={() => setMobileOpen(false)}
          />
          {/* Drop-down nav panel */}
          <div className="fixed inset-x-0 top-16 z-50 flex flex-col gap-1 border-b border-sidebar-border bg-sidebar p-2">
            {navItems.map((item) => {
              const isActive = isItemActive(item);
              return (
                <Link
                  key={item.label}
                  to={item.href}
                  onClick={handleNavClick}
                  className={navLinkClassName(item, isActive)}
                >
                  <item.icon className="h-4 w-4 shrink-0" />
                  <span>{item.label}</span>
                  {item.disabled && (
                    <span className="ml-auto text-[10px] uppercase tracking-wider text-muted-foreground">
                      Soon
                    </span>
                  )}
                </Link>
              );
            })}

            {session?.user && (
              <div className="mt-1 border-t border-sidebar-border px-3 pt-2">
                <p className="text-sm font-medium text-sidebar-foreground truncate">
                  {session.user.name}
                </p>
                <p className="text-xs text-muted-foreground truncate">{session.user.email}</p>
              </div>
            )}
          </div>
        </>
      )}
    </header>
  );
}
